import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  Flame, MessageSquare, Heart, Clock, Target, Sparkles, ExternalLink
} from "lucide-react";
import { cn } from "@/lib/utils";

const MOOD_COLORS = {
  positive: "bg-emerald-400 dark:bg-emerald-500",
  neutral: "bg-amber-400 dark:bg-amber-500",
  negative: "bg-red-400 dark:bg-red-500",
};

const DAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function CalendarWidget() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [calData, setCalData] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/calendar/month?year=${year}&month=${month}`, { headers })
      .then(r => setCalData(r.data))
      .catch(() => {});
    axios.get(`${API}/calendar/suggestions`, { headers })
      .then(r => setSuggestions(r.data.suggestions || []))
      .catch(() => {});
  }, [token, year, month]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // Build calendar grid
  const grid = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    let startDow = firstDay.getDay(); // 0=Sun
    startDow = startDow === 0 ? 6 : startDow - 1; // Mon=0
    const totalDays = lastDay.getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) cells.push(d);
    return cells;
  }, [year, month]);

  const today = now.getDate();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;

  const getDayData = (day) => {
    if (!day || !calData) return null;
    const key = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return calData.days?.[key] || null;
  };

  const SUGGESTION_ICONS = {
    "calendar-check": CalendarIcon, flame: Flame, heart: Heart,
    clock: Clock, target: Target,
  };

  return (
    <div data-testid="calendar-widget" className="w-full rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-border/30">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-primary" strokeWidth={2} />
          <span className="text-sm font-semibold">{MONTHS[month - 1]} {year}</span>
          {calData?.streak > 0 && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-xs font-bold text-orange-600 dark:text-orange-400">
              <Flame className="w-3.5 h-3.5" strokeWidth={2.5} /> {calData.streak}d
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="cal-prev" onClick={prevMonth} className="p-1 rounded-lg hover:bg-muted/50 transition-colors">
            <ChevronLeft className="w-4 h-4 text-muted-foreground" />
          </button>
          <button data-testid="cal-next" onClick={nextMonth} className="p-1 rounded-lg hover:bg-muted/50 transition-colors">
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
          <button
            data-testid="cal-expand"
            onClick={() => navigate("/calendar")}
            className="p-1 rounded-lg hover:bg-muted/50 transition-colors ml-0.5"
            title="Full calendar"
          >
            <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Mini Calendar Grid */}
      <div className="px-3 pt-2 pb-2.5">
        <div className="grid grid-cols-7 gap-0.5">
          {DAYS.map(d => (
            <div key={d} className="text-center text-xs font-semibold text-muted-foreground/70 py-1">{d}</div>
          ))}
          {grid.map((day, i) => {
            const data = getDayData(day);
            const isToday = isCurrentMonth && day === today;
            const hasMood = data?.mood?.length > 0;
            const hasChat = data?.chats?.length > 0;
            const hasEvent = data?.events?.length > 0;
            const topMood = hasMood ? data.mood[0].label : null;

            return (
              <button
                key={i}
                data-testid={day ? `cal-day-${day}` : undefined}
                onClick={() => day && navigate(`/calendar?date=${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`)}
                disabled={!day}
                className={cn(
                  "relative w-full aspect-square flex flex-col items-center justify-center rounded-lg transition-all text-sm",
                  day ? "hover:bg-muted/60 cursor-pointer" : "invisible",
                  isToday && "ring-2 ring-primary font-bold text-primary bg-primary/5"
                )}
              >
                <span>{day}</span>
                {/* Activity dots */}
                {day && (hasMood || hasChat || hasEvent) && (
                  <div className="flex items-center gap-[3px] mt-[2px]">
                    {hasMood && <span className={cn("w-1.5 h-1.5 rounded-full", MOOD_COLORS[topMood] || "bg-gray-400")} />}
                    {hasChat && <span className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500" />}
                    {hasEvent && <span className="w-1.5 h-1.5 rounded-full bg-violet-400 dark:bg-violet-500" />}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3 mt-2 px-1">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground/70"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" /> Mood</span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground/70"><span className="w-2.5 h-2.5 rounded-full bg-blue-400" /> Chat</span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground/70"><span className="w-2.5 h-2.5 rounded-full bg-violet-400" /> Event</span>
        </div>
      </div>

      {/* Smart Suggestions */}
      {suggestions.length > 0 && (
        <div className="border-t border-border/30 px-3 py-2.5 space-y-2">
          <p className="text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5" /> Suggestions
          </p>
          {suggestions.slice(0, 2).map((s, i) => {
            const SIcon = SUGGESTION_ICONS[s.icon] || Sparkles;
            return (
              <button
                key={i}
                data-testid={`cal-suggestion-${i}`}
                onClick={() => {
                  if (s.action === "mood_checkin") navigate("/mood");
                  else if (s.action === "view_goals") navigate("/goals");
                  else if (s.action === "view_event") navigate("/calendar");
                }}
                className="w-full flex items-start gap-2.5 p-2.5 rounded-xl hover:bg-muted/50 transition-all text-left"
              >
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <SIcon className="w-3.5 h-3.5 text-primary" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium leading-tight">{s.title}</p>
                  <p className="text-[11px] text-muted-foreground/60 leading-tight mt-0.5">{s.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
